import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {concat, Observable, of} from 'rxjs';
import {debounceTime, map} from 'rxjs/operators';
import {InputAbstract} from '../base/input.abstract';
import {GroupedSelectOptions, Icons, InputError, SELECT_SEARCH_FUNCTION, SELECT_VALUE_MATCHER, SelectOption, utils} from '../../../../global';
import { MatAutocompleteTrigger, MatAutocomplete, MatOptgroup, MatOption } from '@angular/material/autocomplete';
import { MatFormField, MatLabel, MatPrefix, MatSuffix, MatHint, MatError } from '@angular/material/form-field';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { SvgIconComponent } from '../../svg-icon/svg-icon.component';
import { MatInput } from '@angular/material/input';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatRipple } from '@angular/material/core';
import { MatTooltip } from '@angular/material/tooltip';
import { LoaderComponent } from '../../loader/loader.component';

@Component({
    selector: 'slm-select-grouped-autocomplete-input',
    templateUrl: './select-grouped-autocomplete-input.component.html',
    styles: [`
    :host {
      flex-direction: row;
      box-sizing: border-box;
      display: flex;
    }
  `],
    imports: [MatFormField, NgIf, MatLabel, TranslateDirective, SvgIconComponent, MatPrefix, MatInput, FormsModule, MatAutocompleteTrigger, ReactiveFormsModule, MatSuffix, MatRipple, MatTooltip, LoaderComponent, MatHint, MatError, MatAutocomplete, NgFor, MatOptgroup, MatOption, AsyncPipe, TranslatePipe]
})
export class SelectGroupedAutocompleteInputComponent
  extends InputAbstract
  implements OnInit {
  @Input() autoFocus = false;
  @Input() buttonIcon = true;
  @Input() enterEnabled = false;
  @Input() prefixIcon: Icons = null;
  @Input() suffixIcon: Icons = null;
  @Input() suffixIconLabel = '';
  @Input() maxDisplayLength = 36;
  @Input() clearButton = false;
  @Input() hideSubLabel = false;
  @Input() notFoundOptionLabel: string = null;

  @Input() errors: Array<InputError> = [];
  @Input() searchFunction: SELECT_SEARCH_FUNCTION = (search, option) =>
    (option?.label || '').toLowerCase().includes((search || '').toLowerCase());
  @Input() secondaryValueMatcher: SELECT_VALUE_MATCHER;

  @Input('options')
  public set setOptions(options: Array<GroupedSelectOptions>) {
    this.options = options;
    this.loading = !options;
    const value = this.control?.value;

    if(this.secondaryValueMatcher && this.options && value){
      let primaryMatch = null;
      let secondaryMatch = null;

      this.options.forEach(groups => (groups.options || []).find(
        (option) => {
          const valueId = +(value?.value?.id || value?.id || value.value || value);
          if (+option.value?.id === valueId || +option.value?.id === +valueId || +option.value === +valueId) {
            primaryMatch = option;
            return;
          }
          if (!secondaryMatch && this.secondaryValueMatcher(value, option)) {
            secondaryMatch = option;
          }
        }));

      if (primaryMatch || secondaryMatch) {
        this.control.setValue(primaryMatch || secondaryMatch);
      } else if (
        !!options?.length &&
        !!value &&
        typeof value !== 'object'
      ) {
        const match = (this.options || []).reduce<GroupedSelectOptions[]>((acc, group) => {
          const filtered = (group.options || []).filter(option =>
            this.searchFunction(value, option)
          );

          if (filtered.length > 0) {
            acc.push({ ...group, options: filtered });
          }

          return acc;
        }, []);

        if (!!match) {
          this.control.setValue(match);
        } else {
          this.control.setValue(null);
        }
      }
    }
    this.control.setValue(this.control.value);
  }

  private options: Array<GroupedSelectOptions> = [];
  public options$: Observable<Array<GroupedSelectOptions>>;
  public loading = true;

  @ViewChild('inputAutoComplete', {read: MatAutocompleteTrigger}) inputAutoComplete: MatAutocompleteTrigger;

  ngOnInit() {
    super.ngOnInit();

    this.options$ = concat(of(''), this.control.valueChanges).pipe(
      debounceTime(350),
      map((value: string) => {
        if (!value || typeof value !== 'string') {
          return this.options || [];
        }

        return (this.options || []).reduce<GroupedSelectOptions[]>((acc, group) => {
          const filtered = (group.options || []).filter(option =>
            this.searchFunction(value, option)
          );

          if (filtered.length > 0) {
            acc.push({ ...group, options: filtered });
          }

          return acc;
        }, []);
      }),
      map(result => [...result,  ...(!!this.notFoundOptionLabel ? [{
        groupLabel: this.notFoundOptionLabel,
        options: [{label: this.notFoundOptionLabel, value: -1}]
      }] : []) ])
    );

  }

  displayValue = () => (selected: SelectOption | string): string => {
    let label: string;
    if (!selected) {
      return '';
    }

    if (utils.isObject(selected)) {
      const selectedOption = selected as SelectOption;
      if (selectedOption?.label || selectedOption?.subLabel) {
        label = SelectGroupedAutocompleteInputComponent.getOptionLabel(selectedOption, this.hideSubLabel);
      } else {
        label = this.control?.value ?? '';
      }
    } else {

    }

    return label?.length > +this.maxDisplayLength ? `${label.substring(0, +this.maxDisplayLength - 3)}...` : label;
  };

  public static getOptionLabel = ((option: SelectOption, hideSubLabel: boolean = false): string =>
    !hideSubLabel && option?.subLabel && option?.label ? `${option.label} - ${option.subLabel}` : option?.label || option?.subLabel);

  showDropDown() {
    if (this.inputAutoComplete && this.inputAutoComplete?.autocomplete?.closed) {
      this.inputAutoComplete.openPanel();
    }
  }

  clear() {
    this.control.reset();
  }

  trackItem(item) {
    return item.value;
  };
}
